import type {
  Channel,
  KeeperState,
  LegendItem
} from "./MitigationDemoCards.types"

// Demo-only content for the kodemsecurity "Exploit Validations & Mitigations"
// concept. Gated to a single issue (see TARGET_RISK_ID). Net-new Keeper /
// mitigation concepts are hardcoded here; real record fields (package name,
// fix version, related applications) are wired at render time.
export const TARGET_RISK_ID = "GHSA-9qr9-h5gf-34mp"

// Enriched score breakdown shown for the demo issue, in the drawer and the
// triage list, so both stay in sync.
export const DEMO_KODEM_SCORE = 921
export const DEMO_EPSS = 0.77804

// Project that owns the demo issue; its aggregate score is enriched to match.
export const DEMO_PROJECT_NAME = "kodem-demo/api-agents"

// Runtime evidence summary shown for the demo issue (Usage = Runtime).
export const DEMO_RUNTIME_EVIDENCE_TEXT =
  "Package loaded in runtime, correlated across 5 applications"

// Lockfile changed by the demo fix, and its GitHub source link.
export const DEMO_LOCKFILE_NAME = "pnpm-lock.yaml"
export const DEMO_LOCKFILE_LINE = 96
export const DEMO_LOCKFILE_GITHUB_URL =
  "https://github.com/kodem-demo/api-agents/blob/main/pnpm-lock.yaml#L96"

// Configuration-change mitigation for CVE-2025-55182: scope & size-limit
// Server Actions via next.config (no app code). api-agents has no next.config
// yet, so the fix creates one. ESM ("type":"module") → export default.
export const DEMO_CONFIG_FILE = "next.config.js"
export const DEMO_CONFIG_ALLOWED_ORIGIN = "api-agents.kodemsecurity.com"
export const DEMO_CONFIG_BODY_LIMIT = "512kb"
export const DEMO_CONFIG_GITHUB_URL =
  "https://github.com/kodem-demo/api-agents/blob/main/next.config.js"

// Micro-patch target: react-server-dom-webpack is bundled inside next, so the
// guard lands in next's compiled server build under node_modules (not a first
// party file). api-agents runs on Node, so the node server build.
export const DEMO_MICROPATCH_FILE =
  "node_modules/next/dist/compiled/react-server-dom-webpack/react-server-dom-webpack-server.node.production.js"
export const DEMO_MICROPATCH_NPM_URL =
  "https://www.npmjs.com/package/next/v/15.4.7"

// Third-party rule: a WAF / gateway rule (ModSecurity/Coraza) blocking
// prototype-ref Server-Action bodies. Not SDLC-specific — no repo/dep file.
export const DEMO_RULE_FILE = "waf/rules/rsc-prototype-ref.conf"

export const LEGEND: LegendItem[] = [
  { kind: "upgrade", label: "Upgrade" },
  { kind: "micropatch", label: "Micro-patch" },
  { kind: "config", label: "Configuration change" },
  { kind: "rule", label: "Third-party rule" }
]

// `{fix}` and `{pkg}` are replaced with real record values at render time.
export const CHANNELS: Channel[] = [
  {
    badge: "PR",
    badgeTone: "pr",
    name: "Open PR",
    sub: "Source change, lands as a pull request",
    actions: [
      {
        kind: "upgrade",
        target: "fix",
        label: "Upgrade to {pkg} {fix}",
        metas: [{ tone: "warn", text: "Major version upgrade" }],
        tip: "Major update pulling in the fixed react-server-dom-webpack. Upstream fix; review for App Router API changes."
      },
      {
        kind: "upgrade",
        target: "kd1",
        label: "Upgrade to {pkg} {cur}-KD-1",
        metas: [
          { tone: "reg", text: "Kodem Package Registry" },
          { tone: "ok", text: "No API breaking changes" }
        ],
        tip: "A drop-in hardened build of {pkg} from the Kodem Package Registry, staying on the current major line. Backports the react-server-dom-webpack deserialization fix. Signed and tracked by Kodem."
      },
      {
        kind: "config",
        target: "config",
        label: "Apply Configuration Change",
        metas: [
          { tone: "diff", text: "2 keys, +4 lines in next.config" },
          { tone: "ok", text: "Functionality not impacted" }
        ],
        tip: "next.config serverActions: allowedOrigins + bodySizeLimit, constraining the RSC request path. No code logic change."
      }
    ]
  },
  {
    badge: "CI",
    badgeTone: "ci",
    name: "Harden build artifact",
    sub: "Baked into the image at CI/CD build",
    actions: [
      {
        kind: "micropatch",
        target: "micropatch",
        label: "Apply Kodem micro-patch",
        metas: [
          { tone: "diff", text: "Single-function guard, +3 lines" },
          { tone: "ok", text: "No first party code change" }
        ],
        tip: "Bakes the react-server-dom-webpack own-property reference guard into the built image, refusing prototype-chain refs in the flight payload."
      },
      {
        kind: "config",
        target: "config",
        label: "Apply Configuration Change",
        metas: [
          { tone: "diff", text: "2 keys, +4 lines in next.config" },
          { tone: "ok", text: "Functionality not impacted" }
        ],
        tip: "next.config serverActions allowedOrigins + bodySizeLimit, committed and baked into the image at CI/CD build."
      }
    ]
  },
  {
    badge: "RT",
    badgeTone: "rt",
    name: "Runtime mitigation",
    sub: "In-memory via the Kodem sensor",
    actions: [
      {
        kind: "micropatch",
        target: "micropatch",
        label: "Apply Kodem micro-patch",
        metas: [
          { tone: "diff", text: "Single-function guard, +3 lines" },
          { tone: "ok", text: "No first party code change" }
        ],
        tip: "Kodem sensor applies the own-property reference guard in-memory on the running app. No rebuild, reversible."
      },
      {
        kind: "config",
        target: "config",
        label: "Apply Configuration Change",
        metas: [
          { tone: "diff", text: "2 keys, +4 lines in next.config" },
          { tone: "ok", text: "Functionality not impacted" }
        ],
        tip: "next.config serverActions allowedOrigins + bodySizeLimit applied live via the deployment environment. No rebuild."
      },
      {
        kind: "rule",
        target: "rule",
        label: "Apply third-party rule",
        metas: [
          { tone: "rule", text: "1 gateway rule, request-body filter" },
          { tone: "ok", text: "No app or image change" }
        ],
        tip: "WAF / gateway rule blocking __proto__ / constructor in the Server-Action body. Not SDLC-specific."
      }
    ]
  }
]

// Generalized rationale, in the words of the CVE-2025-55182 mitigation
// analysis: each change neither misses the exploit nor breaks legitimate
// behavior.
export const TRUST_POINTS = [
  "Each change is derived from a verified, sandbox-reproduced exploit, so it neither misses the exploit nor breaks legitimate behavior.",
  "Legitimate same-origin actions with normal payloads — and genuine client references that name a real own export — pass through byte-for-byte unchanged.",
  "Only the crafted payload the exploit relies on (a reference reaching a non-own property such as constructor → Function, or a body carrying __proto__ / constructor / prototype) is refused before decode."
]

export const TRUST_RULE =
  "if(!Object.prototype.hasOwnProperty.call(mod, name)) throw 'Invalid module export reference'"

// Keeper state per application, keyed by `name/environment`. Falls back to
// "monitored" for apps not in this map.
export const KEEPER_STATE_BY_APP: Record<string, KeeperState> = {
  "api-agents/production": "live",
  "agent-gateway/production": "live",
  "agent-scheduler/production": "container",
  "api-agents/staging": "monitored",
  "legacy-portal/production": "pending"
}

// Shown when the issue record has no related applications wired yet, so the
// concept always renders the 5-app status spread from the wireframe.
export const DEMO_APPS: { name: string; environment: string }[] = [
  { name: "api-agents", environment: "production" },
  { name: "agent-gateway", environment: "production" },
  { name: "agent-scheduler", environment: "production" },
  { name: "api-agents", environment: "staging" },
  { name: "legacy-portal", environment: "production" }
]
